<script setup>
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { useAlert } from 'dashboard/composables';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import WhatsappConnectionPanel from './WhatsappConnectionPanel.vue';

// Creates a WhatsApp inbox on an unofficial provider ('baileys' or 'zapi'), then shows the QR code right away
const props = defineProps({
  provider: {
    type: String,
    required: true,
    validator: value => ['baileys', 'zapi'].includes(value),
  },
});

const { t } = useLocoI18n();
const router = useRouter();
const store = useStore();

const PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

const form = reactive({
  inboxName: '',
  phoneNumber: '',
  instanceId: '',
  token: '',
  clientToken: '',
  groups: true,
  historyDays: 0,
});
const touched = ref(false);
const isCreating = ref(false);
const inboxId = ref(null);
const isConnected = ref(false);

const isZapi = computed(() => props.provider === 'zapi');
const phoneValid = computed(() => PHONE_REGEX.test(form.phoneNumber.trim()));
const isValid = computed(
  () =>
    form.inboxName.trim() &&
    phoneValid.value &&
    (!isZapi.value || (form.instanceId.trim() && form.token.trim()))
);

const providerConfig = () => {
  const settings = {
    groups: form.groups,
    history_days: Number(form.historyDays) || 0,
  };
  if (!isZapi.value) return { settings };
  return {
    instance_id: form.instanceId.trim(),
    token: form.token.trim(),
    client_token: form.clientToken.trim() || undefined,
    settings,
  };
};

const create = async () => {
  touched.value = true;
  if (!isValid.value) return;

  isCreating.value = true;
  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: form.inboxName.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: form.phoneNumber.trim(),
        provider: props.provider,
        provider_config: providerConfig(),
      },
    });
    inboxId.value = inbox.id;
  } catch (error) {
    useAlert(error.message || t('LOCO.WHATSAPP.SETUP.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const finish = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId.value },
  });
};

const onConnectionUpdate = status => {
  isConnected.value = status.state === 'open';
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <h2 class="text-heading-2 text-n-slate-12">
        {{
          isZapi
            ? t('LOCO.WHATSAPP.ZAPI_SETUP.TITLE')
            : t('LOCO.WHATSAPP.SETUP.TITLE')
        }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{
          isZapi
            ? t('LOCO.WHATSAPP.ZAPI_SETUP.DESCRIPTION')
            : t('LOCO.WHATSAPP.SETUP.DESCRIPTION')
        }}
      </p>
    </div>

    <Banner color="amber" class="w-full">
      {{ t('LOCO.WHATSAPP.SETUP.UNOFFICIAL_WARNING') }}
    </Banner>

    <form
      v-if="!inboxId"
      class="flex flex-col gap-4 max-w-xl"
      @submit.prevent="create"
    >
      <Input
        v-model="form.inboxName"
        :label="t('LOCO.WHATSAPP.SETUP.INBOX_NAME')"
        :placeholder="t('LOCO.WHATSAPP.SETUP.INBOX_NAME_PLACEHOLDER')"
        :message="
          touched && !form.inboxName.trim()
            ? t('LOCO.WHATSAPP.SETUP.REQUIRED')
            : ''
        "
        :message-type="touched && !form.inboxName.trim() ? 'error' : 'info'"
      />
      <Input
        v-model="form.phoneNumber"
        :label="t('LOCO.WHATSAPP.SETUP.PHONE_NUMBER')"
        placeholder="+5511999999999"
        :message="
          touched && !phoneValid
            ? t('LOCO.WHATSAPP.SETUP.PHONE_NUMBER_ERROR')
            : t('LOCO.WHATSAPP.SETUP.PHONE_NUMBER_HELP')
        "
        :message-type="touched && !phoneValid ? 'error' : 'info'"
      />
      <template v-if="isZapi">
        <Input
          v-model="form.instanceId"
          :label="t('LOCO.WHATSAPP.ZAPI_SETUP.INSTANCE_ID')"
          :message="
            touched && !form.instanceId.trim()
              ? t('LOCO.WHATSAPP.SETUP.REQUIRED')
              : ''
          "
          :message-type="touched && !form.instanceId.trim() ? 'error' : 'info'"
        />
        <Input
          v-model="form.token"
          type="password"
          :label="t('LOCO.WHATSAPP.ZAPI_SETUP.TOKEN')"
          :message="
            touched && !form.token.trim()
              ? t('LOCO.WHATSAPP.SETUP.REQUIRED')
              : ''
          "
          :message-type="touched && !form.token.trim() ? 'error' : 'info'"
        />
        <Input
          v-model="form.clientToken"
          type="password"
          :label="t('LOCO.WHATSAPP.ZAPI_SETUP.CLIENT_TOKEN')"
        />
      </template>

      <SettingsToggleSection
        v-model="form.groups"
        :header="t('LOCO.WHATSAPP.OPTIONS.GROUPS')"
        :description="t('LOCO.WHATSAPP.OPTIONS.GROUPS_DESC')"
      />
      <Input
        v-if="!isZapi"
        v-model="form.historyDays"
        type="number"
        min="0"
        max="365"
        :label="t('LOCO.WHATSAPP.OPTIONS.HISTORY_DAYS')"
        :message="t('LOCO.WHATSAPP.OPTIONS.HISTORY_DAYS_DESC')"
      />

      <div>
        <Button
          type="submit"
          :label="
            isZapi
              ? t('LOCO.WHATSAPP.ZAPI_SETUP.SUBMIT')
              : t('LOCO.WHATSAPP.SETUP.SUBMIT')
          "
          :is-loading="isCreating"
        />
      </div>
    </form>

    <template v-else>
      <WhatsappConnectionPanel
        :inbox-id="inboxId"
        @update="onConnectionUpdate"
      />
      <div class="flex gap-3">
        <Button
          :label="t('LOCO.WHATSAPP.SETUP.CONTINUE')"
          :disabled="!isConnected"
          @click="finish"
        />
        <Button
          v-if="!isConnected"
          faded
          slate
          :label="t('LOCO.WHATSAPP.SETUP.CONNECT_LATER')"
          @click="finish"
        />
      </div>
    </template>
  </div>
</template>
